import type { Mutation } from "@bp/pim-graphql-types";
import type { DataFields, UpdateResolver } from "@urql/exchange-graphcache";
import { groupedUpdateList } from "./groupedUpdateList";

export type mutations = (keyof Mutation)[];
export type FieldName = string;

/**
 * Builds a graphcache update resolver for a mutation that adds to / removes from
 * a cached list query (`fieldName`). On delete it unlinks the affected uuids from
 * every cached list; on create/connect it appends the new entity cache keys.
 * Update mutations need no resolver — the normalized entity is patched from the
 * response.
 */
const createUpdateResolver =
  (fieldName: FieldName, mutation: keyof Mutation): UpdateResolver =>
  (result, args, cache, info) => {
    const isDelete = /delete.*/.test(mutation);

    const typeName = mutation.match(
      /(set|create|delete|disconnect|merge|bulkUpdateOrCreate|addMembersTo|removeMembersFrom|updateMembersRoleIn)(Bp)?(.+?)s?$/,
    )?.[3];

    if (!typeName) throw new Error("typeName cannot be calculated");
    const inspect = cache.inspectFields("Query");
    const filteredInspects = inspect.filter((i) => i.fieldName === fieldName);
    const cacheResults = filteredInspects.map((i) => ({
      ...i,
      cache: cache.resolve("Query", i.fieldKey) as string[],
    }));

    if (isDelete) {
      const uuids = getUuidFromArgs(args, typeName);

      if (uuids) {
        // we know which uuids were deleted → unlink them from every cached list
        cacheResults.forEach((r) => {
          const linkArray = r.cache.filter((c) => !uuids.includes(c));
          cache.link("Query", r.fieldKey, linkArray);
        });
      } else {
        // delete keyed by something other than uuid/id (e.g. clientId) and no
        // uuid in the response → we can't surgically unlink, so invalidate the
        // list fields to force a refetch
        filteredInspects.forEach((i) => cache.invalidate("Query", i.fieldName, i.arguments));
      }
      return result;
    }

    const newKeys = getUuidsFromResponse(result, info.fieldName, typeName);
    if (newKeys.length === 0) {
      // couldn't derive cache keys from the response (e.g. an entity without a
      // uuid in its selection) → invalidate the list so it refetches
      filteredInspects.forEach((i) => cache.invalidate("Query", i.fieldName, i.arguments));
    } else {
      cacheResults.forEach((r) => {
        const linkArray = [...r.cache, ...newKeys];
        cache.link("Query", r.fieldKey, linkArray);
      });
    }
    return result;
  };

export const updateFunctions = Object.fromEntries(
  Object.entries(groupedUpdateList).flatMap(([fieldName, mutations]) =>
    mutations.map((mutation) => [mutation, createUpdateResolver(fieldName, mutation)]),
  ),
);

const getUuidsFromResponse = (
  result: DataFields,
  fieldName: FieldName,
  typeName: string,
): string[] => {
  const response = result?.[fieldName];
  const uuids: string[] = [];

  // Recursively collect `${typeName}:${uuid}` cache keys from the response.
  const findUuids = (obj: unknown) => {
    if (!obj || typeof obj !== "object") return;

    const hasTypeAndUuid = (value: unknown): value is { __typename?: string; uuid: string } => {
      return (
        typeof value === "object" &&
        value !== null &&
        "uuid" in value &&
        (("__typename" in value && typeof value.__typename === "string") ||
          !("__typename" in value))
      );
    };

    if (hasTypeAndUuid(obj) && obj.__typename === typeName && obj.uuid) {
      uuids.push(`${typeName}:${obj.uuid}`);
    }

    if (Array.isArray(obj)) {
      obj.forEach(findUuids);
    } else {
      Object.values(obj).forEach(findUuids);
    }
  };

  if (!response) return [];

  findUuids(response);
  return uuids;
};

type ArgsWithUuid = {
  uuid?: string;
  id?: string;
  where?: {
    uuid?: string;
    id?: string;
  };
};

/** Returns the deleted cache keys, or null when the args don't carry a uuid/id. */
const getUuidFromArgs = (args: unknown, typeName: string): string[] | null => {
  const hasUuidOrId = (value: unknown): value is ArgsWithUuid => {
    if (!value || typeof value !== "object") return false;

    const obj = value as Record<string, unknown>;
    return (
      ("uuid" in obj && typeof obj.uuid === "string") ||
      ("id" in obj && typeof obj.id === "string") ||
      ("where" in obj &&
        typeof obj.where === "object" &&
        obj.where !== null &&
        (("uuid" in obj.where && typeof obj.where.uuid === "string") ||
          ("id" in obj.where && typeof obj.where.id === "string")))
    );
  };

  if (!hasUuidOrId(args)) {
    return null;
  }

  const uuid = args.where?.uuid ?? args.uuid ?? args.where?.id ?? args.id;
  return [`${typeName}:${uuid}`];
};
